import { useCallback, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useSelection } from '@/contexts/SelectionContext';
import { supabase } from '@/integrations/supabase/client';

const DISMISSED_KEY = 'feed-optimizer-onboarding-dismissed';

/**
 * Read localStorage reactively.
 *
 * Der frühere Schreiber dieses Schlüssels — die OnboardingChecklist als FAB — ist
 * mit dem Hauptpfad-Schnitt (Task 7) entfallen. Der Lesepfad bleibt bewusst
 * stehen: bestehende Konten tragen das Flag noch, und für sie soll der
 * Feedback-Knopf sichtbar bleiben. Neue Konten erreichen `true` nur noch über den
 * ehrlichen Weg unten (alle Schritte wirklich erledigt).
 */
function useDismissedState(): boolean {
  const subscribe = useCallback((cb: () => void) => {
    // Listen for storage events from other tabs AND custom events from same tab
    window.addEventListener('storage', cb);
    window.addEventListener('onboarding-dismissed', cb);
    return () => {
      window.removeEventListener('storage', cb);
      window.removeEventListener('onboarding-dismissed', cb);
    };
  }, []);
  const getSnapshot = useCallback(() => localStorage.getItem(DISMISSED_KEY) === 'true', []);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * Returns true when the user is through the setup, i.e. account connected,
 * products pulled, optimized and pushed live (or the legacy dismiss flag is set).
 */
export function useOnboardingComplete(): boolean {
  const { user } = useAuth();
  const { selectedGoogleAccountId, selectedFeedId } = useSelection();
  const dismissed = useDismissedState();

  const { data: progress } = useQuery({
    queryKey: ['onboarding-complete-check', selectedFeedId],
    queryFn: async () => {
      if (!selectedFeedId) return { productCount: 0, hasOptimized: false, hasUploaded: false };

      const [productsRes, optimizedRes, uploadedRes] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('feed_id', selectedFeedId).limit(1),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('feed_id', selectedFeedId).eq('optimization_status', 'completed').limit(1),
        supabase.from('upload_jobs').select('id', { count: 'exact', head: true }).eq('feed_id', selectedFeedId).eq('status', 'completed').limit(1),
      ]);

      return {
        productCount: productsRes.count || 0,
        hasOptimized: (optimizedRes.count || 0) > 0,
        hasUploaded: (uploadedRes.count || 0) > 0,
      };
    },
    enabled: !!user && !!selectedFeedId && !dismissed,
    refetchInterval: 10000,
    staleTime: 5000,
  });

  if (dismissed) return true;
  if (!progress) return false;

  const allStepsComplete =
    !!selectedGoogleAccountId &&
    progress.productCount > 0 &&
    progress.hasOptimized &&
    progress.hasUploaded;

  return allStepsComplete;
}
